// ----------------------------------------------------------------------
// <copyright file="Izenda.Izenda.ColumnDataObject.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ColumnDataObject
 */

import { action, observable, computed } from 'mobx';
import { placeholder, filter, isEmpty, map, sumBy, flatMap, get, concat, sortBy, set, clone, flow } from 'lodash/fp';

const COLUMN_TYPE = {
  ROW: 'row',
  COLUMN: 'column',
  SIDE_TOTAL: 'side total',
  VALUE: 'value'
};

class ColumnDataObject {
  @observable isExpand = true;

  @computed
  get title() {
    if (!this.isLeaf) {
      return this.value;
    } else if (this.forcePrefix) {
      return this.sideTotalTitle;
    }

    return this.element.field.fieldNameAlias;
  }

  @computed
  get pivotColumn() {
    if (this.type !== COLUMN_TYPE.COLUMN) {
      return 0;
    }
    return sumBy('pivotColumn', this.childs) || 1;
  }

  @computed
  get sideTotalColumns() {
    if (this.type !== COLUMN_TYPE.COLUMN) {
      return [];
    }
    return flow(flatMap('sideTotalColumns'), concat(placeholder, this.sideTotals))(this.childs);
  }

  @computed
  get sideTotalTitle() {
    return String.format('{0} {1}', this.prefix, get('field.fieldNameAlias', this.element));
  }

  constructor(isColumnHeader) {
    // check if this node is leaf or branch
    this.isLeaf = true;
    this.key = '';
    this.keyList = [];
    this.element = null;
    this.value = '';
    this.childs = [];
    this.sideTotals = [];
    this.level = 1; // decrease from max > 1
    this.forcePrefix = false;
    this.isInvisible = false;
    this.type = COLUMN_TYPE.COLUMN;
    this.uniqueFieldNameAlias = '';
    this.groupFieldLevel = '';
    this.keyPath = [];
    this.isColumnHeader = isColumnHeader || false;
  }

  @action
  toggleExpand() {
    this.isExpand = !this.isExpand;
  }

  /**
   * [toLeafNode Convert base ColumnDataObject to leafNode]
   * @method toLeafNode
   * @param  {int}   level   [level]
   * @param  {array of json}   keyPath [filter value]
   * @param  {json}   item    [base data to build node]
   * @param  {string}     prefix  [Prefix for side total column name]
   * @return {ColumnDataObject}           [this object]
   */
  toLeafNode(level, keyPath, prefix, type, item, isColumnHeader) {
    this.isLeaf = true;
    this.key = item.columnName;
    this.element = item.element;
    this.uniqueFieldNameAlias = item.element.field.fieldNameAlias;
    this.keyPath = keyPath;
    this.prefix = prefix;
    this.type = type;
    this.groupFieldLevel = item.groupFieldLevel;
    this.isColumnHeader = isColumnHeader || false;
    return this;
  }

  /**
   * [toBranchNode convert base ColumnDataObject to branchNode]
   * @method toBranchNode
   * @param  {Array json}         valueMapping      [Mapping for value container to correct field in data]
   * @param  {int}         level             [level of object]
   * @param  {Array of json}         sideTotalsMapping [Mapping for side total]
   * @param  {array of json}         keyPath           [filter value]
   * @param  {json}         item              [base data to build node]
   * @return {ColumnDataObject}                           [ColumnDataObject with isLeaf = false]
   */
  @action
  toBranchNode(valueMapping, level, sideTotalsMapping, keyPath, item) {
    // create branch node for each sub group
    let subKeyPath;
    let childBranchArray;
    const firstItemKey = item.key[0];

    this.isLeaf = false;
    this.key = firstItemKey.columnName;
    this.element = firstItemKey.element;
    this.value = firstItemKey.value;
    this.data = item.data;
    this.level = level;

    this.keyPath = subKeyPath = concat(keyPath || [], [[this.key, this.value]]);
    childBranchArray = map(
      createBranchNode.bind(null, valueMapping, level - 1, sideTotalsMapping, subKeyPath),
      item.subGroup
    );

    if (!isEmpty(childBranchArray)) {
      this.childs = childBranchArray;
      const newSideTotalsMapping = flow(
        filter({ groupFieldLevel: firstItemKey.element.field.fieldNameAlias }),
        sortBy('position')
      )(sideTotalsMapping);
      this.sideTotals = map(
        createLeafNode.bind(null, level, subKeyPath, this.value, COLUMN_TYPE.SIDE_TOTAL),
        newSideTotalsMapping
      );
    } else {
      const newValueMapping = map(
        itemValue => set('groupFieldLevel', firstItemKey.element.field.fieldNameAlias, clone(itemValue)),
        valueMapping
      );
      this.childs = map(createLeafNode.bind(null, 1, subKeyPath, '', COLUMN_TYPE.VALUE), newValueMapping);
    }

    return this;
  }
}

/**
 * [createLeafNode Create new leaf node object]
 * @method createLeafNode
 * @param  {int}       level   [level of item]
 * @param  {array of json}       keyPath [filter value to get correct data]
 * @param  {json}       item    [Based data to build object]
 * @param  {string}     prefix  [Prefix for side total column name]
 * @return {ColumnDataObject}               [ColumnDataObject with isLeaf = true]
 */
const createLeafNode = (level, keyPath, prefix, type, item, isColumnHeader) =>
  new ColumnDataObject().toLeafNode(level, keyPath, prefix, type, item, isColumnHeader);

/**
 * [createBranchNode Create new branch node object]
 * @method createBranchNode
 * @param  {Array json}         valueMapping      [Mapping for value container to correct field in data]
 * @param  {int}         level             [level of object]
 * @param  {Array of json}         sideTotalsMapping [Mapping for side total]
 * @param  {array of json}         keyPath           [filter value]
 * @param  {json}         item              [base data to build node]
 * @return {ColumnDataObject}                           [ColumnDataObject with isLeaf = false]
 */
const createBranchNode = (valueMapping, level, sideTotalsMapping, keyPath, item) =>
  new ColumnDataObject().toBranchNode(valueMapping, level, sideTotalsMapping, keyPath, item);

export default {
  ColumnDataObject,
  createBranchNode,
  createLeafNode,
  COLUMN_TYPE
};



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ColumnDataObject.js